/**
 * Native Bridge Error Classes
 *
 * 브릿지 통신 중 발생할 수 있는 에러들을 정의
 */

import type { BridgeResponse } from './types'

export type BridgeErrorCode =
  | 'TIMEOUT'
  | 'NATIVE_ERROR'
  | 'PLATFORM_NOT_SUPPORTED'
  | 'INVALID_RESPONSE'
  | 'REQUEST_NOT_FOUND'

/**
 * 브릿지 통신 관련 에러 클래스
 */
export class BridgeError extends Error {
  public readonly code: BridgeErrorCode
  public readonly response?: BridgeResponse
  public readonly requestId?: string

  constructor(code: BridgeErrorCode, message?: string, response?: BridgeResponse, requestId?: string) {
    super(message || getErrorMessage(code))
    this.name = 'BridgeError'
    this.code = code
    this.response = response
    this.requestId = requestId
  }

  /**
   * 네이티브 에러 응답으로부터 BridgeError 생성
   */
  static fromNativeError(response: BridgeResponse): BridgeError {
    return new BridgeError('NATIVE_ERROR', `${response.data?.message || 'Unknown error'}`, response, response.requestId)
  }

  /**
   * 타임아웃 에러 생성
   */
  static timeout(requestId: string, timeoutMs: number): BridgeError {
    return new BridgeError('TIMEOUT', `Request timeout after ${timeoutMs}ms`, undefined, requestId)
  }

  /**
   * 플랫폼 미지원 에러 생성
   */
  static platformNotSupported(): BridgeError {
    return new BridgeError('PLATFORM_NOT_SUPPORTED', 'Native platform interface not found')
  }

  /**
   * 응답을 찾을 수 없는 에러 생성
   */
  static requestNotFound(requestId: string): BridgeError {
    return new BridgeError('REQUEST_NOT_FOUND', `Pending request not found: ${requestId}`, undefined, requestId)
  }

  /**
   * 에러가 복구 가능한지 여부
   */
  get isRecoverable(): boolean {
    return this.code === 'TIMEOUT' || this.code === 'NATIVE_ERROR'
  }

  /**
   * 에러 상세 정보를 객체로 반환
   */
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      requestId: this.requestId,
      response: this.response,
      isRecoverable: this.isRecoverable
    }
  }
}

/**
 * 에러 코드별 기본 메시지 반환
 */
function getErrorMessage(code: BridgeErrorCode): string {
  const messages: Record<BridgeErrorCode, string> = {
    TIMEOUT: 'Request timeout',
    NATIVE_ERROR: 'Native application error',
    PLATFORM_NOT_SUPPORTED: 'Platform not supported',
    INVALID_RESPONSE: 'Invalid response format',
    REQUEST_NOT_FOUND: 'Request not found'
  }

  return messages[code]
}

/**
 * 에러 타입 가드 함수
 */
export function isBridgeError(error: unknown): error is BridgeError {
  return error instanceof BridgeError
}

/**
 * 에러 로깅 헬퍼
 * 서버 사이드에서만 로그 파일에 기록됨
 */
export async function logBridgeError(error: BridgeError, context?: string): Promise<void> {
  // 서버 사이드에서만 로거 사용
  if (typeof window === 'undefined') {
    try {
      const { getLogger } = await import('@/lib/logger')
      const logger = getLogger()
      logger.error(
        {
          code: error.code,
          message: error.message,
          requestId: error.requestId,
          isRecoverable: error.isRecoverable,
          response: error.response,
          context
        },
        `Bridge Error: ${error.name}`
      )
    } catch {
      // 로거 로드 실패 시 fallback
      console.error(`[Bridge${context ? `:${context}` : ''}] ${error.name}:`, {
        code: error.code,
        message: error.message,
        requestId: error.requestId,
        isRecoverable: error.isRecoverable,
        response: error.response
      })
    }
  } else {
    // 클라이언트 사이드에서는 console만 사용
    const prefix = context ? `[Bridge:${context}]` : '[Bridge]'
    console.error(`${prefix} ${error.name}:`, {
      code: error.code,
      message: error.message,
      requestId: error.requestId,
      isRecoverable: error.isRecoverable,
      response: error.response
    })
  }
}
